import { ACCORDION_NAME, accordionNameToIndex } from '../components/LEAccordion/utils';
import { getTempInfoCountryConfig } from '../constants/countries';
import {
  ImportType,
  LeExperianErrorCodeEnum,
  Maybe,
  PayoffRequestStatus,
  TemporaryData,
  TransformType,
} from '../gql/generated/graphql';
import { LAST_ACCORDION_ITEM_INDEX } from '../pages/LoanApp';

export const sellsViaMarketplace = (
  data?: Maybe<Pick<TemporaryData, 'wants_to_sell' | 'country'>>,
) => data?.wants_to_sell === true && getTempInfoCountryConfig(data).marketplaceFlow;

export const hasCobuyer = (tempInfoData?: TemporaryData) => {
  const filteredObject =
    Object.fromEntries(
      Object.entries(tempInfoData ?? {}).filter(([key]) => key.startsWith('cobuyer_')),
    ) ?? {};

  return Object.values(filteredObject).some((value) => value !== null && value !== '');
};

export const deleteCobuyerData = (tempInfoData?: TemporaryData) => {
  const data = { ...tempInfoData };
  const cobuyerKeys = Object.keys(data).filter((key) => key.startsWith('cobuyer_'));

  const cobuyerValues = {
    ...Object.fromEntries(cobuyerKeys.map((key) => [key, null])),
    current_accordion_panel: `${
      accordionNameToIndex[ACCORDION_NAME.LOAN_APP_ACCORDION]
    }:${LAST_ACCORDION_ITEM_INDEX}`,
  };

  return cobuyerValues;
};

export const payoffRequestInProgressStatuses = [
  PayoffRequestStatus.Pending,
  PayoffRequestStatus.Fetching,
];

export const payoffRequestFailureStatuses = [
  PayoffRequestStatus.Failed,
  PayoffRequestStatus.FailureRequested,
];

export const getTransformType = (
  failedPrequal: boolean,
  hasKbbValues: boolean,
  hasOtherLienholder: boolean,
  frozenOrLockedCredit: boolean,
  wantsToSell: boolean,
  payoffRequestStatus?: PayoffRequestStatus | null,
  isUnsupportedLienholder?: boolean,
) => {
  const isPayoffRequestInProgress =
    payoffRequestStatus && payoffRequestInProgressStatuses.includes(payoffRequestStatus);
  const isPayoffRequestFailure =
    payoffRequestStatus && payoffRequestFailureStatuses.includes(payoffRequestStatus);

  if (wantsToSell) {
    return TransformType.AcquisitionNotApproved;
  }
  if (isPayoffRequestInProgress) {
    return TransformType.PayoffRequestInProgress;
  }
  if (isPayoffRequestFailure) {
    return TransformType.PayoffRequestFailure;
  }
  if (isUnsupportedLienholder) {
    return TransformType.PiUnavailable;
  }
  if (hasOtherLienholder) {
    return TransformType.OtherLienholder;
  }
  if (!hasKbbValues) {
    return TransformType.NoKbbFailure;
  }
  if (frozenOrLockedCredit) {
    return TransformType.FrozenOrLockedCredit;
  }
  if (failedPrequal) {
    return TransformType.SoftPullFailure;
  }

  return TransformType.AutoStructureBegin;
};

const transformTypeToImportType: Partial<Record<TransformType, ImportType>> = {
  [TransformType.AcquisitionNotApproved]: ImportType.AcquisitionFlowFinished,
  [TransformType.PayoffRequestInProgress]: undefined,
  [TransformType.PayoffRequestFailure]: ImportType.PayoffRequestFailure,
  [TransformType.PiUnavailable]: ImportType.PiUnavailable,
  [TransformType.OtherLienholder]: ImportType.OtherLienholder,
  [TransformType.NoKbbFailure]: ImportType.NoKbbValuesFailure,
  [TransformType.FrozenOrLockedCredit]: ImportType.FrozenOrLockedCredit,
  [TransformType.SoftPullFailure]: ImportType.SoftPullFailure,
  [TransformType.Canada]: ImportType.Canada,
  [TransformType.AutoStructureBegin]: undefined,
};

export const getImportType = (transformType: TransformType) =>
  transformTypeToImportType[transformType];

export const frozenOrLockedCredit = (prequalificationErrors: Maybe<string>[]) => {
  return (
    prequalificationErrors?.some(
      (error) =>
        error === LeExperianErrorCodeEnum.Frozen || error === LeExperianErrorCodeEnum.Locked,
    ) || false
  );
};
